/**
 * What a reload onto a new build would cut short. Work — a write in flight, a
 * message on screen, a run of requests such as an upload batch — is never cut
 * short, whichever page holds it. A read only matters to the page being left,
 * since the page arrived at reads again after the reload. The reload waits for
 * a later change of page while either is held.
 *
 * Something the reader watches without touching anything — a video, a quiz on
 * a projector — holds only the reload a quiet tab takes by itself, since a
 * change of page ends it anyway.
 */

export enum ReloadHoldKind {
  Read = 'read',
  Work = 'work',
  Watched = 'watched',
}

const holds: Record<ReloadHoldKind, number> = {
  [ReloadHoldKind.Read]: 0,
  [ReloadHoldKind.Work]: 0,
  [ReloadHoldKind.Watched]: 0,
};

/** Holds the reload until the returned release is called; a second call is a no-op. */
export const holdReload = (kind = ReloadHoldKind.Work) => {
  holds[kind] += 1;
  let isReleased = false;
  return () => {
    if (!isReleased) {
      isReleased = true;
      holds[kind] -= 1;
    }
  };
};

export const isReloadHeld = () => holds[ReloadHoldKind.Read] + holds[ReloadHoldKind.Work] > 0;

export const isWorkHeld = () => holds[ReloadHoldKind.Work] > 0;

export const isAnyReloadHeld = () => Object.values(holds).some(count => count > 0);

/**
 * One hold for a component, taken and released over and over and holding at
 * most once at a time. It takes nothing while the component is not mounted, so
 * a callback that outlives it cannot hold the reload with nothing left to
 * release it.
 */
export const createReloadHold = () => {
  let isMounted = false;
  let release: (() => void) | undefined;

  const releaseHold = () => {
    release?.();
    release = undefined;
  };

  return {
    take: () => {
      if (isMounted) {
        release ??= holdReload();
      }
    },
    release: releaseHold,
    /** For the component's mount effect, whose cleanup the returned function is. */
    mount: () => {
      isMounted = true;
      return () => {
        isMounted = false;
        releaseHold();
      };
    },
  };
};

/** Holds the reload for the whole of a run of requests and the work between them. */
export const whileHoldingReload = async <T>(work: () => Promise<T>): Promise<T> => {
  const release = holdReload();
  try {
    return await work();
  } finally {
    release();
  }
};
